import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { Injectable } from "@angular/core";
import { LoggerService } from "@bfx/logger";
import { BenchmarkDataPrimaryLookupService } from "./BenchmarkDataPrimaryLookupService";
import { CubiqWorker } from "shared/cubiq-services/worker/CubiqWorker";
import { SysFeatures, SystemConfigPromiseService } from "shared/auth-services";
import { Subject, first } from "rxjs";
import { BmarkFixingData } from "shared/cubiq-services/model/benchmarks/BmarkFixingData.model";
import { ArrayDataSource } from "@angular/cdk/collections";
import { NotificationService } from "../../notification/impl/NotificationService";

const logger = LoggerService.for(__dirname);

export class BenchmarkPositionHedgeModeData {
  public userId: CubiqTS.Long;

  public hedgeMode: CubiqTS.HedgingModeType;
}

@Injectable()
export class BenchmarkPositionLookupService {
  protected positionMap: Map<string, BenchmarkPositionHedgeModeData> = new Map();

  public changePositionSubject: Subject<CubiqTS.ChangeHedgingModeEventImpl> = new Subject();

  constructor(
    protected cubiqWorker: CubiqWorker,
    protected sysConfigService: SystemConfigPromiseService,
    protected benchmarkDataService: BenchmarkDataPrimaryLookupService,
    protected notificationService: NotificationService,
  ) {
    this.init();
  }

  protected init(): void {
    Promise.all([
      this.cubiqWorker.isAuth(`${this.constructor.name}`),
      this.sysConfigService.isPrimed(),
    ]).then(() => {
      this.sysConfigService.getSystemConfig(SysFeatures.bmAutoHedging).then((value: string) => {
        let autoHedgeEnabled: boolean = value != null && ("Y" == value || "y" == value);
        if (autoHedgeEnabled) {
          this.subscribeForFixingResponse();
          this.subscribeForEvents();
        } else {
          logger.out.info(
            `${this.constructor.name}: Auto hedging disabled. ` +
              `Disabling listening for auto hedge mode events.`,
          );
        }
      });
    });
  }

  protected subscribeForFixingResponse(): void {
    logger.out.debug(`${this.constructor.name}: Subscribing for status requests ...`);

    this.benchmarkDataService.fixingStatusResponseSubject.subscribe((response) => {
      if (Array.isArray(response?.manualHedgingPositions)) {
        response.manualHedgingPositions.forEach(this.processChangeModeEvent.bind(this));
      }
    });

    this.benchmarkDataService.recentFixingSubject.subscribe((response) => {
      let req = new CubiqTS.ManualPositionHedgingStatusRequestImpl();
      req.bmTimeCodes = response;
      this.cubiqWorker.postServiceRequest(req).then((response: CubiqTS.StandardReportImpl) => {
        if (response != null && Array.isArray(response.results)) {
          response.results.forEach(this.processChangeModeEvent.bind(this));
        }
      });
    });
  }

  protected subscribeForEvents(): void {
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.ChangeHedgingModeEventImpl.name)
      .subscribe((event: CubiqTS.ChangeHedgingModeEventImpl) => {
        try {
          this.processChangeModeEvent(event);
        } catch (e) {
          let bmCode: string = event?.fixing?.code;
          let bmTime: CubiqTS.Moment = event?.fixing?.time;

          logger.out.error(`${this.constructor.name}: Failed to process
                    change code ${bmCode}, time ${bmTime}`);
        }
      });
  }

  protected processChangeModeEvent(event: CubiqTS.ChangeHedgingModeEventImpl): void {
    if (event != null && event.fixing != null && event.pairs != null) {
      logger.out.info(
        `${this.constructor.name}: Received change mode event for fixing ` +
          `${event.fixing.code} @ ${event.fixing.time?.format("YYYYMMDD HH:mm")}, ` +
          `total number for pairs ${event.pairs.length}`,
      );

      let dupEvent = Object.assign(new CubiqTS.ChangeHedgingModeEventImpl(), event);
      dupEvent.pairs = [];

      event.pairs.forEach((pair: string) => {
        let positionKey: string = this.getPositionKey(event.fixing, pair);

        // We consider all positions as auto if we don't have info on them
        let mode: CubiqTS.HedgingModeType = CubiqTS.HedgingModeType.AUTO;
        if (this.positionMap.has(positionKey)) {
          let positionData = this.positionMap.get(positionKey);
          if (positionData != null) {
            mode = positionData.hedgeMode;
          }
        }

        let modeChanged: boolean = mode != event.hedgingModeType;

        let pData: BenchmarkPositionHedgeModeData = new BenchmarkPositionHedgeModeData();
        Object.assign(pData, { hedgeMode: event.hedgingModeType, userId: event.userId });
        this.positionMap.set(positionKey, pData);

        if (modeChanged) {
          logger.out.info(
            `${this.constructor.name}: Position hedging mode changed: ` +
              ` ${positionKey} to ${CubiqTS.HedgingModeType[event.hedgingModeType]}`,
          );
          dupEvent.pairs.push(pair);
        }
      });

      if (dupEvent.pairs.length > 0) {
        this.changePositionSubject.next(dupEvent);
      }
    } else {
      logger.out.warn(
        `${this.constructor.name}: Change hedging mode event missing fixing ` +
          `or pairs information`,
      );
    }
  }

  protected getPositionKey(fixing: CubiqTS.BMTimeCodeImpl, pair: string): string {
    return `${BmarkFixingData.generateFixingIdFromCodeMoment(
      fixing?.code,
      fixing?.time,
      null,
    )}${pair}`;
  }

  public getHedgingMode(positionKey: string): CubiqTS.HedgingModeType {
    let data = this.positionMap.get(this.sanitizePositionKey(positionKey));
    return data?.hedgeMode;
  }

  public getHedgingModeChangeUserId(positionKey: string): CubiqTS.Long {
    let data = this.positionMap.get(this.sanitizePositionKey(positionKey));
    return data?.userId;
  }

  protected sanitizePositionKey(positionKey: string, replace: string = ""): string {
    return positionKey.replace(":ACTIVE", replace).replace(":RECENT", replace);
  }

  public changeAutoHedgeForPositions(
    bmTimeCode: CubiqTS.BMTimeCodeImpl,
    pairs: Array<string>,
    hedgingModeType: CubiqTS.HedgingModeType = CubiqTS.HedgingModeType.MANUAL,
  ): void {
    if (Array.isArray(pairs) && pairs.length > 0) {
      let request = new CubiqTS.ChangePositionHedgingTypeRequestImpl();

      request.hedgingModeType = hedgingModeType;
      request.bmTimeCode = bmTimeCode;
      request.ccyPairs = pairs;

      this.cubiqWorker.postServiceRequest(request).then((value) => {
        if (value instanceof CubiqTS.ErrorsImpl) {
          this.notificationService.handleGenericError(value);
        } else if (value instanceof CubiqTS.ChangePositionHedgingTypeResponseImpl) {
          let response: CubiqTS.ChangePositionHedgingTypeResponseImpl = value;
          if (Array.isArray(response.updates) && response.updates.length > 0) {
            response.updates.forEach((update) => {
              this.notificationService.handleGenericError(update.errors);
            });
          }
        }
      });
    }
  }
}
